import React, { useEffect, useState } from 'react';
import { Loader2Icon } from 'lucide-react';
import { PanelShell } from '../../ui/PanelShell';
import { SectionTitle } from '../../ui/SectionTitle';
import { FieldRow } from '../../ui/FieldRow';
import { NumberField } from '../../ui/NumberField';
import { PillButton } from '../../ui/PillButton';
import { SupportCategory } from './SupportCategory';
import { SupportStructure } from './SupportStructure';
import { ManualPlacement, type ManualTool } from './ManualPlacement';
import { ProfileBar } from './profiles/ProfileBar';
import { useSupportProfiles } from '../../../hooks/useSupportProfiles';
import type { SupportSettings } from '../../../types/supportProfiles';
import { MinimaIcon, OverhangsIcon, StabilizationIcon, ReinforcementsIcon } from '../../icons/ToolIcons';
import type { SupportState } from '../../../types/tools';
type SupportsPanelProps = {
  onClose: () => void;
  supportState: SupportState;
  onGenerate: () => void;
  onClear: () => void;
  onManualChange: (manual: boolean) => void;
};
const MANUAL_DEFAULTS = {
  brushRadius: 4,
  spacing: 2
};
export function SupportsPanel({
  onClose,
  supportState,
  onGenerate,
  onClear,
  onManualChange
}: SupportsPanelProps) {
  const [placement, setPlacement] = useState<'auto' | 'manual'>('auto');
  const {
    settings,
    updateSettings
  } = useSupportProfiles();
  const [manualTool, setManualTool] = useState<ManualTool>('create');
  const [brushRadius, setBrushRadius] = useState(MANUAL_DEFAULTS.brushRadius);
  const [spacing, setSpacing] = useState(MANUAL_DEFAULTS.spacing);
  const [minimaSupported, setMinimaSupported] = useState(false);
  const set = (key: 'minima' | 'overhangs' | 'stabilization' | 'reinforcements') => (v: boolean) => updateSettings({
    [key]: v
  } as Partial<SupportSettings>);
  const generating = supportState === 'generating';
  const manual = placement === 'manual';
  const hasEdits = minimaSupported || brushRadius !== MANUAL_DEFAULTS.brushRadius || spacing !== MANUAL_DEFAULTS.spacing;
  useEffect(() => {
    onManualChange(manual);
  }, [manual, onManualChange]);
  useEffect(() => () => onManualChange(false), [onManualChange]);
  const discardEdits = () => {
    setBrushRadius(MANUAL_DEFAULTS.brushRadius);
    setSpacing(MANUAL_DEFAULTS.spacing);
    setMinimaSupported(false);
  };
  const generateLabel = manual ? 'Generate Structure' : 'Auto-Generate All';
  return <PanelShell title="Supports" onClose={onClose} footer={<>
          {manual ? <PillButton disabled={!hasEdits || generating} onClick={discardEdits} data-id="element-605" data-mp="1q2uds32s2gwv">
              Discard Edits
            </PillButton> : <PillButton disabled={supportState !== 'generated'} onClick={onClear} data-id="element-606" data-mp="1bdeokriwajja">
              Clear All
            </PillButton>}
          <PillButton variant="primary" disabled={generating} onClick={() => {
      onGenerate();
      if (manual) setMinimaSupported(false);
    }} data-id="element-607" data-mp="242lwgh48olh7">
          
            {generating && <Loader2Icon className="h-3 w-3 animate-spin" data-id="element-608" data-mp="3tifu9aqcgqbd" />}
            {generating ? 'Generating…' : generateLabel}
          </PillButton>
        </>} data-id="element-604" data-mp="1l2ya64xzsrhu">
      
      <ProfileBar data-id="element-609" data-mp="73wp84n9enhk" />
      <SectionTitle data-id="element-610" data-mp="74ent7xe9dsn">Touchpoint Placement</SectionTitle>
      <div role="radiogroup" aria-label="Touchpoint placement" className="grid grid-cols-2 bg-white p-[1px]" data-id="element-611" data-mp="75ze2lcoeakp">
        {(['auto', 'manual'] as const).map(p => <button key={p} type="button" role="radio" aria-checked={placement === p} onClick={() => setPlacement(p)} className={`h-[22px] rounded-[2px] text-[12px] transition-colors duration-150 ${placement === p ? 'bg-pf-blue text-white' : 'text-[#6a6f75] hover:bg-pf-panel'}`} data-id="element-612" data-mp="3alodb7y9ynah">
          
            {p === 'auto' ? 'Auto Placement' : 'Manual Placement'}
          </button>)}
      </div>

      {manual ? <ManualPlacement tool={manualTool} onToolChange={setManualTool} brushRadius={brushRadius} onBrushRadiusChange={setBrushRadius} spacing={spacing} onSpacingChange={setSpacing} onSupportAllMinima={() => setMinimaSupported(true)} data-id="element-613" data-mp="usfojuobzpxi" /> : <>
          <FieldRow label="Touchpoint Size" info className="mt-[2px]" data-id="element-614" data-mp="1z3keyu7iufob">
            <NumberField ariaLabel="Touchpoint size" value={settings.touchpointSize} onChange={v => updateSettings({
          touchpointSize: v
        })} unit="mm" step={0.05} data-id="element-615" data-mp="243ycvjfes98m" />
          </FieldRow>

          <SupportCategory tone="orange" label="Minima" icon={<MinimaIcon className="h-full w-full" data-id="element-617" data-mp="2d2k2tzn7mqjf" />} checked={settings.minima} onToggle={set('minima')} data-id="element-616" data-mp="2d3v700kki0b2" />
          <SupportCategory tone="teal" label="Overhangs" icon={<OverhangsIcon className="h-full w-full" data-id="element-619" data-mp="2mug23e118mp8" />} checked={settings.overhangs} onToggle={set('overhangs')} data-id="element-618" data-mp="2hvd58gv7hmsc">
            <FieldRow label="Density" info className="min-h-[28px]" data-id="element-620" data-mp="2rtsqi37jy1tx">
              <NumberField ariaLabel="Overhang density" value={settings.overhangDensity} onChange={v => updateSettings({
            overhangDensity: v
          })} step={0.01} className="w-[80px]" data-id="element-621" data-mp="2ws3zyctxqabm" />
            
            </FieldRow>
            <FieldRow label="Angle" info className="min-h-[28px]" data-id="element-622" data-mp="g80jteiovl4b">
              <NumberField ariaLabel="Overhang angle" value={settings.overhangAngle} onChange={v => updateSettings({
            overhangAngle: v
          })} decimals={0} max={90} unit="°" step={1} className="w-[80px]" data-id="element-623" data-mp="b733l7h8p15g" />
            
            </FieldRow>
          </SupportCategory>
          <SupportCategory tone="pink" label="Stabilization" icon={<StabilizationIcon className="h-full w-full" data-id="element-625" data-mp="u32qi3vd1ei6" />} checked={settings.stabilization} onToggle={set('stabilization')} data-id="element-624" data-mp="u4dqdsloq240" />
          <SupportCategory tone="green" label="Reinforcements" icon={<ReinforcementsIcon className="h-full w-full" data-id="element-627" data-mp="1mnt5ieemr6t9" />} checked={settings.reinforcements} onToggle={set('reinforcements')} data-id="element-626" data-mp="1mm9o2kh8mppw">
            <FieldRow label="Density" info className="min-h-[28px]" data-id="element-628" data-mp="3c33ucgt2bxlj">
              <NumberField ariaLabel="Reinforcement density" value={settings.reinforcementDensity} onChange={v => updateSettings({
            reinforcementDensity: v
          })} step={0.01} className="w-[80px]" data-id="element-629" data-mp="3c4ye3rslljo1" />
            
            </FieldRow>
          </SupportCategory>
        </>}

      <SectionTitle data-id="element-630" data-mp="2tc49nrslp9y2">Support Structure</SectionTitle>
      <SupportStructure data-id="element-631" data-mp="2tegqu2qjbga2" />
    </PanelShell>;
}